import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { latestAttr, registryOptions, humanizeToken, type AttrMap } from '../lib/inlineAttribution';
import { formatPhone } from '../lib/formatPhone';
import { Badge, Button, Input, IconPencil } from '../atoms';
import { InlineEditCard, CreateEntityModal, EntityGroupsPanel, LastUpdated, useToast, type EntityContact } from '../organisms';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * ContactDetail — /contacts/:id
 *
 * Bespoke card profile for a single CRM contact, modeled on ClientDetail's
 * OverviewTab: per-section InlineEditCards (edit-in-place → PUT /api/contacts/:id)
 * with per-section attribution from GET /api/field-attribution/contacts/:id.
 *
 * The WRINKLE (2.2): a contact's phone/email do NOT live on the contacts row —
 * they sit in the polymorphic phone_numbers / email_addresses side tables and are
 * edited through the bespoke entity-contacts route, which is HOST-scoped
 * (:entityType/:entityId/:contactId). A standalone contact page has no inherent
 * host, so the dedicated ContactMethodsCard discovers one from the contact's
 * contact_list links (GET /api/contact-list?contact_id=<id>) and reads/writes the
 * methods through that host — never the generic contacts PUT, which can't reach
 * the side tables.
 */

interface Contact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  courtesy: string | null;
  goes_by: string | null;
  type: string | null;
  status: 'active' | 'inactive';
  client_id: string | null;
  title: string | null;
  department: string | null;
  office: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

// A row of the contact_list junction (GET /api/contact-list?contact_id=<id>).
interface ContactLink {
  id: string;
  entity_type: string;
  entity_id: string;
  contact_id: string;
  role: string | null;
  is_inherited?: number | boolean | null;
}

// The host entity_types the entity-contacts route can reach (crm.js ENTITY_TABLES).
const SUPPORTED_HOSTS = new Set(['clients', 'assessors', 'collectors', 'jurisdictions', 'properties', 'parcels']);

const contactName = (c: Contact): string =>
  [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name || '—';

const statusVariant: Record<Contact['status'], 'success' | 'neutral'> = {
  active: 'success', inactive: 'neutral',
};

export function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editAllOpen, setEditAllOpen] = useState(false);

  const contactQuery = useQuery<Contact>({
    queryKey: ['contacts', id],
    queryFn: ({ signal }) => api.get<Contact>(`/api/contacts/${id}`, signal),
    enabled: !!id,
  });

  // Gate the edit affordances on the contact grant (mirrors the server). Hook —
  // before any early return so hook order stays stable across load → loaded.
  const canEdit = useEntityPermission('contact', 'can_edit');

  if (contactQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading contact…</p>;
  }

  if (contactQuery.error) {
    const notFound = contactQuery.error instanceof ApiClientError && contactQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Contact not found' : 'Failed to load contact'}</h2>
        <p style={{ margin: 0 }}>{(contactQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/contacts')}>
            Back to Contacts
          </Button>
        </p>
      </div>
    );
  }

  if (!contactQuery.data) return null;

  const contact = contactQuery.data;

  return (
    <>
      <EntityHeader
        prefix="Contact"
        title={contactName(contact)}
        subtitle={<>
          {contact.goes_by ? <span>Goes by {contact.goes_by}</span> : null}
          <LastUpdated updatedBy={contact.updated_by} updatedAt={contact.updated_at} />
        </>}
        statusPills={<>
          {contact.type && <Badge variant="neutral">{humanizeToken(contact.type)}</Badge>}
          <Badge variant={statusVariant[contact.status]} dot>{humanizeToken(contact.status)}</Badge>
        </>}
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate('/contacts')}>
              Back
            </Button>
            {canEdit && (
              // Fallback to the full-record form so contact fields not surfaced in
              // the cards below stay reachable (mirrors ClientDetail/PropertyDetail).
              <Button variant="secondary" size="sm" onClick={() => setEditAllOpen(true)}>
                Edit all fields
              </Button>
            )}
          </>
        }
      />

      <OverviewCards contact={contact} canEdit={canEdit} />

      <div style={{ marginTop: 'var(--sp-5)' }}>
        <EntityGroupsPanel entityType="contact" entityId={contact.id} />
      </div>

      <CreateEntityModal
        open={editAllOpen}
        onClose={() => setEditAllOpen(false)}
        table="contacts"
        recordId={contact.id}
        invalidateKeys={[['contacts-list'], ['contacts', contact.id], ['field-attribution', 'contacts', contact.id]]}
      />
    </>
  );
}

function OverviewCards({ contact, canEdit }: { contact: Contact; canEdit: boolean }) {
  // Per-FIELD attribution so each box derives its own "last updated by · when".
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'contacts', contact.id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/contacts/${contact.id}`, signal),
  });
  const attr = attrQuery.data ?? {};
  const record = contact as unknown as Record<string, unknown>;
  const invalidate: unknown[][] = [['contacts', contact.id], ['contacts-list'], ['field-attribution', 'contacts', contact.id]];

  // Contact Type is an org-configurable registry (org-settings 'contact-types') —
  // same source the client-profile add-contact form uses.
  const contactTypes = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'contact-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/contact-types', signal),
    staleTime: 5 * 60 * 1000,
  });

  // The contact's Client association (contacts.client_id → clients.name). Every
  // contact belongs to a client, so the Identity card resolves + shows the client
  // NAME in read mode (never the raw id) and offers a typeahead client picker on
  // edit — same GET /api/clients the property/client pickers use.
  const clientsQuery = useQuery<{ id: string; name: string }[]>({
    queryKey: ['clients-list'],
    queryFn: ({ signal }) => api.get<{ id: string; name: string }[]>('/api/clients', signal),
    staleTime: 5 * 60 * 1000,
  });
  const clientOptions = (clientsQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }));
  const clientNameById = new Map((clientsQuery.data ?? []).map((c) => [c.id, c.name] as const));

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
      gap: 'var(--sp-4)',
    }}>
      <InlineEditCard
        title="Identity"
        routePath="contacts"
        recordId={contact.id}
        record={record}
        canEdit={canEdit}
        invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['first_name', 'last_name', 'client_id', 'courtesy', 'goes_by', 'type', 'status'])}
        fields={[
          { key: 'first_name', label: 'First name' },
          { key: 'last_name', label: 'Last name' },
          { key: 'client_id', label: 'Client', type: 'searchable-select', options: clientOptions, display: (v) => (v ? (clientNameById.get(String(v)) ?? '…') : '—') },
          { key: 'courtesy', label: 'Courtesy' },
          { key: 'goes_by', label: 'Goes by' },
          { key: 'type', label: 'Type', type: 'select', options: registryOptions(contactTypes.data), addOptionSlug: 'contact-types', display: (v) => (v ? humanizeToken(String(v)) : '—') },
          { key: 'status', label: 'Status', type: 'select', options: statusOptions, display: (v) => (v ? humanizeToken(String(v)) : '—') },
        ]}
      />

      <InlineEditCard
        title="Role & employment"
        routePath="contacts"
        recordId={contact.id}
        record={record}
        canEdit={canEdit}
        invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['title', 'department', 'office'])}
        fields={[
          { key: 'title', label: 'Title' },
          { key: 'department', label: 'Department' },
          { key: 'office', label: 'Office' },
        ]}
      />

      <ContactMethodsCard contact={contact} canEdit={canEdit} />
    </div>
  );
}

/**
 * Pick a host link the entity-contacts route can reach. Prefer a direct (non-
 * inherited) client link — the most stable host for a client rep — then any
 * direct link, then any supported link. is_inherited comes back 0/1.
 */
function pickHost(links: ContactLink[]): ContactLink | null {
  const supported = links.filter((l) => SUPPORTED_HOSTS.has(l.entity_type));
  const direct = supported.filter((l) => !l.is_inherited);
  return direct.find((l) => l.entity_type === 'clients')
    || direct[0]
    || supported[0]
    || null;
}

/**
 * ContactMethodsCard — the dedicated (non-InlineEditCard) box for a contact's
 * primary email + phone, which live in the side tables the generic contacts PUT
 * can't reach. It discovers a host from the contact's contact_list links and
 * reads/writes the methods through the bespoke entity-contacts route:
 *
 *   read : GET /api/contacts-by-entity/:entityType/:entityId  (host list; find me)
 *   save : PUT /api/entity-contacts/:entityType/:entityId/:contactId
 *
 * The save must carry the contact's identity (first/last/company) because that
 * route rewrites the whole contact + its primary email/phone in one transaction;
 * omitting a name would blank it. Styled identically to InlineEditCard.
 */
function ContactMethodsCard({ contact, canEdit }: { contact: Contact; canEdit: boolean }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ email: string; phone: string }>({ email: '', phone: '' });

  // 1) The contact's links → a reachable host. contact_id is a supported FK
  //    filter on the generic list, so this returns just this contact's links.
  const linksQuery = useQuery<ContactLink[]>({
    queryKey: ['contact-links', contact.id],
    queryFn: ({ signal }) => api.get<ContactLink[]>(`/api/contact-list?contact_id=${contact.id}`, signal),
    enabled: !!contact.id,
  });
  const host = useMemo(() => pickHost(linksQuery.data ?? []), [linksQuery.data]);

  // 2) Read the contact's primary email/phone from the host's contact list (the
  //    same enriched read EntityContactsPanel uses). The contact is guaranteed to
  //    appear — the host was derived from one of its own links.
  const methodsQuery = useQuery<EntityContact[]>({
    queryKey: ['contact-methods', contact.id, host?.entity_type, host?.entity_id],
    queryFn: ({ signal }) => api.get<EntityContact[]>(`/api/contacts-by-entity/${host!.entity_type}/${host!.entity_id}`, signal),
    enabled: !!host,
  });
  const me = (methodsQuery.data ?? []).find((r) => r.id === contact.id) ?? null;
  const email = me?.email ?? null;
  const phone = me?.phone_number ?? null;

  const editable = canEdit && !!host;

  // Seed the draft each time we enter edit mode.
  useEffect(() => {
    if (!editing) return;
    setDraft({ email: email ?? '', phone: phone ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const save = useMutation({
    mutationFn: () => {
      // Carry the contact's identity so the whole-contact rewrite preserves it.
      const body: Record<string, string> = {
        first_name: contact.first_name ?? '',
        last_name: contact.last_name ?? '',
        company_name: contact.company_name ?? '',
        email: draft.email.trim(),
        phone: draft.phone.trim(),
      };
      return api.put(`/api/entity-contacts/${host!.entity_type}/${host!.entity_id}/${contact.id}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-methods', contact.id] });
      qc.invalidateQueries({ queryKey: ['contact-links', contact.id] });
      qc.invalidateQueries({ queryKey: ['contacts', contact.id] });
      qc.invalidateQueries({ queryKey: ['field-attribution', 'contacts', contact.id] });
      toast({ variant: 'success', message: 'Contact methods updated.' });
      setEditing(false);
    },
    onError: (err) => toast({
      variant: 'error',
      message: err instanceof ApiClientError ? err.message : 'Could not update contact methods.',
    }),
  });

  return (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0, fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
        }}>Contact methods</h3>
        {editable && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Edit Contact methods"
            title="Edit Contact methods"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent',
              cursor: 'pointer', color: 'var(--brand-primary)', fontSize: 'var(--fs-xs)',
              fontWeight: 'var(--fw-medium)', fontFamily: 'inherit', padding: 0,
            }}
          >
            <IconPencil size={13} /> Edit
          </button>
        )}
      </div>

      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Email</span>
            <Input
              type="email"
              value={draft.email}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Phone</span>
            <Input
              inputMode="tel"
              placeholder="XXX-XXX-XXXX"
              value={draft.phone}
              onChange={(e) => setDraft((d) => ({ ...d, phone: formatPhone(e.target.value) }))}
            />
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={save.isPending}>Cancel</Button>
            <Button variant="primary" size="sm" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
          </div>
        </div>
      ) : (
        <div>
          <MethodRow label="Email" value={email ? <a href={`mailto:${email}`} style={{ color: 'var(--brand-primary)', overflowWrap: 'anywhere' }}>{email}</a> : '—'} />
          <MethodRow label="Phone" value={phone ? <a href={`tel:${phone}`} style={{ color: 'var(--brand-primary)' }}>{phone}</a> : '—'} />
          {!linksQuery.isLoading && !host && (
            <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              Contact methods are managed on the record this contact is linked to.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function MethodRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)',
      padding: 'var(--sp-1) 0', fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: 'var(--color-ink-3)', flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  );
}
